import { DataAlertIndicator, DataInfoIndicator } from '_components/indicators';
import { Component, useEffect, useRef, useState } from 'react';
import { LayerGroup, LayersControl, MapContainer, TileLayer, ZoomControl } from 'react-leaflet'
import { useDispatch, useSelector } from 'react-redux';
import { MANUFACTURING_SITE as site }  from 'types/globals'; 
import { mapActions } from '_store';
import { createFeatureCollectionsByType } from '_helpers';
import BusLayer from '_components/layers/bus.layer';
import RouteLayer from '_components/layers/routes.layer';
import PoiLayer from '_components/layers/poi.layer';
import L from 'leaflet';


class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError(error) {
        // Mettre à jour l'état pour afficher l'UI de repli suite à une erreur
        return { hasError: true };
    }
    componentDidCatch(error, errorInfo) {
        // Vous pouvez aussi loguer l'erreur au niveau de votre application
        console.error('Erreur capturée par ErrorBoundary:', error, errorInfo);
    }
    render() {
        if (this.state.hasError) {
            // Vous pouvez afficher n'importe quelle UI de repli
            return <h1>Quelque chose s'est mal passé.</h1>;
        }
        return this.props.children; 
    }
}


export { DataMap };

function DataMap() {
    const [ location ] = useState([site.coord.lat, site.coord.lng])
    const dispatch = useDispatch();

    const { BaseLayer } = LayersControl;
    const layers = require("../../../assets/files/layers.json");
    const lastPositions = useSelector(state => state.map?.lastPositions?.value?.features);
    const layerVisibility = useSelector(state => state.map.layerVisibility);

    const poi = useSelector(state => state.map?.poi);

    const [busesCoordinate, setBusesCoordinates] = useState([]);
    const [alertsCoordinate, setAlertsCoordinate] = useState([]);
    const [stopsCoordinate, setStopsCoordinate] = useState([]);
    const [allCoordinates, setAllCoordinates] = useState([]);
    const [multipleCoordinatesAlerts, setMultipleCoordinatesAlerts] = useState([]);
    const [multipleCoordinatesStops, setMultipleCoordinatesStops] = useState([]);
    const [multipleCoordinatesOthers, setMultipleCoordinatesOthers] = useState([]);

    /* Gestion du centrage de la carte en fonction des résultats affichés */
    const mapRef = useRef(null);

    // Fonction pour ajuster la carte en fonction des éléments
    const fitMapBounds = () => {
        const map = mapRef.current;
        const bounds = allCoordinates.map(location => L.latLng(location.geometry.coordinates[0], location.geometry.coordinates[1]));
        
        if(bounds.length > 0) {
            map.fitBounds(bounds);
        }
    };

    useEffect(() => {
        if (allCoordinates && allCoordinates.length > 0) {
            fitMapBounds();
        }
    }, [allCoordinates]);

    //Récupération des pois au chargement
    useEffect(() => {
        if (!poi){
            dispatch(mapActions.getPoi());
        }
    }, [])

    // Création des FeatureCollections par type
    const featureCollections = poi ? createFeatureCollectionsByType(poi.features) : {};

    useEffect(() => {
        if (lastPositions){
            const noCoord = [];
            const busesCoord = [];
            const multiCoord = [];
            const multiCoordAlerts = [];
            const multiCoordStops = [];
            const multiCoordOthers = [];
            const busesStops = [];
            const busesAlerts = []

            lastPositions.forEach(feature => {
                const coordinates = feature.geometry.coordinates;
                //Elements sans coordonnées - on n'en fait rien pour le moment
                if (!coordinates || coordinates[0][0] === null || coordinates[0][1] === null) {
                    noCoord.push(feature);
                } else if (coordinates.length === 1 && (coordinates[0][0] !== null && coordinates[0][1] !== null)) {
                    //Bus - séparés par niveau d'alerte afin de faciliter l'affichage de chaque type
                    if(feature.properties.URGENCY_LEVEL === '3') {
                        busesAlerts.push({ 
                            ...feature, 
                            geometry: { 
                                ...feature.geometry, 
                                coordinates: [...coordinates] 
                            } 
                        });
                    } else if (feature.properties.URGENCY_LEVEL === '4') {
                        busesStops.push({ 
                            ...feature, 
                            geometry: { 
                                ...feature.geometry, 
                                coordinates: [...coordinates] 
                            } 
                        });
                    } else {
                        busesCoord.push({ 
                            ...feature, 
                            geometry: { 
                                ...feature.geometry, 
                                coordinates: [...coordinates] 
                            } 
                        });
                    }                    
                } else if (coordinates.length > 1) {
                    //Elements à plusieurs coordonnées = trajets
                    multiCoord.push({ 
                        ...feature, 
                        geometry: { 
                            ...feature.geometry, 
                            coordinates: coordinates.map(coord => [...coord]) 
                        } 
                    });
                    if(feature.properties.URGENCY_LEVEL === '3') {
                        multiCoordAlerts.push({ 
                            ...feature, 
                            geometry: { 
                                ...feature.geometry, 
                                coordinates: coordinates.map(coord => [...coord]) 
                            } 
                        });
                        busesAlerts.push({ 
                            ...feature, 
                            geometry: { 
                                ...feature.geometry, 
                                coordinates: [...coordinates] 
                            } 
                        });
                    } else if (feature.properties.URGENCY_LEVEL === '4') {
                        multiCoordStops.push({ 
                            ...feature, 
                            geometry: { 
                                ...feature.geometry, 
                                coordinates: coordinates.map(coord => [...coord]) 
                            } 
                        });
                        busesStops.push({ 
                            ...feature, 
                            geometry: { 
                                ...feature.geometry, 
                                coordinates: [...coordinates] 
                            } 
                        });
                    } else {
                        multiCoordOthers.push({ 
                            ...feature, 
                            geometry: { 
                                ...feature.geometry, 
                                coordinates: coordinates.map(coord => [...coord]) 
                            } 
                        });
                        busesCoord.push({ 
                            ...feature, 
                            geometry: { 
                                ...feature.geometry, 
                                coordinates: [...coordinates] 
                            } 
                        });
                    }            
                }
            });

            //Calques de bus
            setBusesCoordinates(busesCoord);
            setAlertsCoordinate(busesAlerts);
            setStopsCoordinate(busesStops);
            //Regrouppement de tous les éléments pour centrer la carte
            const allCoord = busesCoord.concat(busesAlerts, busesStops)
            setAllCoordinates(allCoord);
            //Calques de tracés
            setMultipleCoordinatesAlerts(multiCoordAlerts);
            setMultipleCoordinatesStops(multiCoordStops);
            setMultipleCoordinatesOthers(multiCoordOthers);
            
        }
    }, [lastPositions])

    const toggleAlertLevel3 = () => {
        const newVisibility = !layerVisibility.alerts;
        dispatch(mapActions.setLayerVisibility({ layerId: 'alerts', visibility: newVisibility }));
    };

    const toggleStopLevel4 = () => {
        const newVisibility = !layerVisibility.stops;
        dispatch(mapActions.setLayerVisibility({ layerId: 'stops', visibility: newVisibility }));
    };


    return (
        <div className='data-map-container'>
            <ErrorBoundary>
                <MapContainer 
                    center={location} 
                    zoom={7} 
                    zoomControl={false}
                    ref={mapRef}
                    >
                    <LayersControl>
                        {layers.map((e, index) =>
                        <BaseLayer key={index} checked={index === 0} name={e.name}>
                            <TileLayer
                                attribution={e.attribution}
                                url={e.url}
                                format={e.format}
                                style={e.style}
                            />
                        </BaseLayer>
                        )}
                    </LayersControl>
                    {/*<TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />*/}
                    {/*********** Affichage des bus sans alerte***********/}
                    {layerVisibility.buses && busesCoordinate && 
                        <>
                            {busesCoordinate.map((feature, index) => {
                                if (feature.geometry.coordinates[0][0] &&  feature.geometry.coordinates[0][1]){
                                    return <BusLayer key={index+'A'} bus={feature}/>
                                } else {
                                    return null
                                }
                            })}
                            {layerVisibility.routes && multipleCoordinatesOthers && multipleCoordinatesOthers.map((feature, index) => (                        
                                <RouteLayer key={index} route={feature} />                                           
                            ))}
                            
                        </>
                    }
                    {/*********** Affichage des bus en alerte 3***********/}
                    {layerVisibility.alerts && alertsCoordinate && 
                        <>
                            {alertsCoordinate.map((feature, index) => {
                                if (feature.geometry.coordinates[0][0] &&  feature.geometry.coordinates[0][1]){
                                    return <BusLayer key={index+'R'} bus={feature}/>
                                } else {
                                    return null
                                }
                            })}
                            {layerVisibility.routes && multipleCoordinatesAlerts && multipleCoordinatesAlerts.map((feature, index) => (                        
                                <RouteLayer key={index} route={feature} />                                           
                            ))}
                        </>
                    }
                    {/*********** Affichage des bus en stop 4***********/}
                    {layerVisibility.stops && stopsCoordinate && 
                        <>
                            {stopsCoordinate.map((feature, index) => {
                                if (feature.geometry.coordinates[0][0] &&  feature.geometry.coordinates[0][1]){
                                    return <BusLayer key={index+'B'} bus={feature}/>
                                } else {
                                    return null
                                }
                            })}
                            {layerVisibility.routes && multipleCoordinatesStops && multipleCoordinatesStops.map((feature, index) => (                        
                                <RouteLayer key={index} route={feature} />                                           
                            ))}
                        </>
                    }
                    {/*********** Affichage des pois ***********/}
                    {featureCollections && Object.keys(featureCollections).map(type => {
                        // Vérification de la visibilité de chaque type de POI
                        const isVisible = (() => {
                            switch (type) {
                                case 'depot':
                                    return layerVisibility.depots;
                                case 'dealer':
                                    return layerVisibility.dealerships;
                                case 'pantographe':
                                case 'charger':
                                    return layerVisibility.pantographs;
                                default:
                                return true;
                            }
                        })();
                        
                        if (!isVisible) return null;

                        return (
                        <LayerGroup key={type}>
                            {featureCollections[type].features.map((feature, index) => {
                                return (  
                                    <PoiLayer key={index} poi={feature} type={type}/>                               
                                );
                            })}
                        </LayerGroup>
                    )})}

                    {/* Affichage des lignes pour les éléments de type "LineString" */}
                    {/*layerVisibility.routes && multipleCoordinates && multipleCoordinates.map((feature, index) => (                        
                        <RouteLayer key={index} route={feature} />                                           
                    ))*/}
                    <ZoomControl position="bottomright" />
                    <div className={"map-legend"}>
                        <DataAlertIndicator 
                            toggleAlertLevel3={toggleAlertLevel3}
                            toggleStopLevel4={toggleStopLevel4}
                        />
                    </div>                
                    <DataInfoIndicator />
                </MapContainer>
            </ErrorBoundary>
        </div>
        
    )
}
